import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import type { GetProps } from 'antd';
import { useNavigate } from 'react-router';
import type { IConvenenteComConvenio, IConvenenteComConvenios, IConvenio } from '../components/Interfaces';

export const color = {
  nomeFantasiaColor: "#fff",
  razaoSocialColor: "#fff",
  cepColor: "#fff",
  enderecoColor: "#fff",
  numeroColor: "#fff",
  ufColor: "#fff",
  municipioColor: "#fff",
  bairroColor: "#fff",
  complementoColor: "#fff",
  telefone1Color: "#fff",
  telefone2Color: "#fff",
  emailColor: "#fff",
};

export const resetColor = () => {
  color.nomeFantasiaColor = "#ffffff";
  color.razaoSocialColor = "#ffffff";
  color.cepColor = "#ffffff";
  color.enderecoColor = "#ffffff";
  color.numeroColor = "#ffffff";
  color.ufColor = "#ffffff";
  color.municipioColor = "#ffffff";
  color.bairroColor = "#ffffff";
  color.complementoColor = "#ffffff";
  color.telefone1Color = "#ffffff";
  color.telefone2Color = "#ffffff";
  color.emailColor = "#ffffff";
};

export const colors = {
  main: "#1890ff",
  blue: "#2160BD",
  white: "#ffffff",
  text: '#262626',
  secondaryWhite: "#ffdada",
  info: "#155BCB",
  danger: "#E60000",
  warning: "#FFCD07",
  success: "#168821",
  secondary01: "#FFFFFF",
  secondary02: "#F8F8F8",
  secondary03: "#EDEDED",
  secondary04: "#CCCCCC",
  secondary05: "#9E9D9D",
  secondary06: "#888888",
  secondary07: "#555555",
  secondary08: "#333333",
  secondary09: "#000000",
  primaryDefault: "#1351B4",
  primaryPastel01: "#C5D4EB",
  primaryPastel02: "#DBE8FB",
  primaryLighten01: "#2670E8",
  primaryLighten02: "#5992ED",
  primaryDarken01: "#0C326F",
  primaryDarken02: "#071D41",
};

export function onlyNumbers(str: string | undefined) {
  if (!str) return "";
  return str.replace(/[^0-9]/g, "");
}

type RangePickerProps = GetProps<typeof DatePicker.RangePicker>;

export const disabledDate: RangePickerProps['disabledDate'] = (current) => {
  // Can not select days after today
  return current && current >= dayjs().endOf('day');
};

export const disabledDateFuture: RangePickerProps['disabledDate'] = (current) => {
  // Can not select days after today and today
  return current && current > dayjs().endOf('day');
};

export function isStateValid(state: any) {
  if (!state) return false; // Makes sure it's not null
  if (typeof state !== "object") return false;
  if (typeof state.data !== "object") return false;
  return true;
}

export function isStateWithoutDataValid(state: any) {
  if (!state) return false; // Makes sure it's not null
  if (typeof state !== "object") return false;
  return true;
}

export function hasText(str: any) {
  if (!str) return false; // Makes sure it's not null
  else if (typeof str === "string") return str.trim().length > 0;
  else return true;
}

export function brToNumber(value: string): number {
  if (!value) return 0;

  // Remove thousands separators
  const noThousands = value.replace(/\./g, "");

  // Replace decimal comma with dot
  const normalized = noThousands.replace(",", ".");

  // Convert to number
  return Number(normalized);
}

/**
 * Valida se um Número Único de Protocolo (NUP) é válido,
 * ignorando formatação e considerando apenas os dígitos numéricos.
 */

export function isValidNup(nup: string | null | undefined): boolean {
  if (!nup) return false;

  const numeric = nup.replace(/\D/g, "");

  let tipo = 0;
  if (numeric.length === 17) tipo = 1 // NUP novo
  if (numeric.length === 15) tipo = 2 // NUP antigo
  if (numeric.length === 10) tipo = 3 // SIPPS
  if (tipo === 0) return false;
  if (tipo == 2 || tipo == 3) return true; // TODO IDENTIFICA E IMPLEMENTAR OS CAUCULOS DESSES DOIS TIPOS

  const nupBase = numeric.slice(0, -2);
  const dvInformado = numeric.slice(-2);

  // Calcular o primeiro DV
  let dv1 = calcularDV(nupBase);

  // Calcular o segundo DV, acrescentando o primeiro DV ao número base
  let nupComDv1: string = nupBase + dv1;
  let dv2: string = calcularDV(nupComDv1).toString();


  let dvCalculado: string = dv1 + dv2;
  //console.log(dv1);
  //console.log(dv2);

  return dvInformado === dvCalculado.toString().padStart(2, "0");
}

/**
 * Calculates a Verifier Digit (DV) for a given string of numbers.
 * This implementation uses a Modulo 11 algorithm with increasing weights (2, 3, 4...).
 * 
 * @param numero The input string containing only digits.
 * @returns The calculated verifier digit as a number (0-9).
 */
function calcularDV(numero: string): number {
  let soma: number = 0;
  let peso: number = 2; // Initial weight

  // Iterate from the end to the beginning of the string (right to left)
  for (let i = numero.length - 1; i >= 0; i--) {
    // Convert char to number. In JS/TS, we can use Number() or parseInt() on the substring/char.
    // Or leverage the unary plus operator (+)
    const digito: number = Number(numero.charAt(i));
    soma += digito * peso;
    peso++;
    // The common implementation uses continuously increasing weights (2, 3, 4, 5...)
  }

  const resto: number = soma % 11;

  let dv: number;

  const tmp: number = 11 - resto;
  if (tmp >= 10) {
    // The original Java logic for when tmp >= 10 resulted in an integer division.
    // If tmp is 10, dv becomes 1. If tmp is 11, dv becomes 1.
    // This specific block seems questionable in the original Java logic for a single digit DV (0-9).
    // A common Modulo 11 rule is often: if remainder is 0 or 1, DV is 0 (or similar variant).
    // Following the *exact* Java code provided:
    dv = Math.floor(tmp / 10);
  } else {
    dv = tmp;
  }

  return dv;
}

export  const formatarData = (str: string | undefined) => {
    return str ? dayjs(str).format("DD/MM/YYYY") : ""
  }


export function pushToBase(navigate: ReturnType<typeof useNavigate>) {
  navigate("/" + import.meta.env.VITE_APP_BASE);
}

export function inverterEstruturaConvenio(
  convenios: IConvenio[]
): IConvenenteComConvenio[] {
  return convenios.flatMap(convenio => {
    const { convenentes, ...convenioSemConvenentes } = convenio;

    return convenentes.map(convenente => ({
      ...convenente,
      convenio: convenioSemConvenentes
    }));
  });
}

// /*
// * Recebe ja o valor do campo como parametro e ve representa um numero valido
// */
// function validaNuProcesso(nuProcessoParam) {
//   tamanho = nuProcessoParam.length;
//   numero = nuProcessoParam;
//   nu_processo = nuProcessoParam;

//   ano = numero.substring(13, 17);
//   anoatual = new Date().getFullYear();

//   //alert("Ano: "+ano+" Hoje: "+anoatual);
//   for (i = 0; i < tamanho; i++) {
//     nu_processo = nu_processo.replace(' ', '');
//   }

//   tamanho = nu_processo.length;
//   /*if (tamanho < 20)
//   {
//     alert("N�mero do Processo TCE n�o est� no padr�o SIPPS.");
//     return false;
//   }*/

//   if (nuProcessoParam.length > 20) {
//     alert("N�mero do Processo TCE n�o est� no padr�o SIPPS");
//     return false;
//   }

//   if ((ano < "1980") || (ano > anoatual)) {
//     alert("O N�mero do Processo possui a seguinte forma��o:\n\nN�mero da Unidade Procotolizadora (5 n�meros) .  \nSeq�encial (6 n�meros) / \n* Ano do Processo (4 n�meros) - \nD�gito Verificador (2 n�meros)\n\n Ano do Processo Incorreto!");
//     return false;
//   }
//   // Rotina que verifica o d�gito verificador
//   if (ano > "1983") {
//     if (nu_processo != "") {
//       numeroComp = nuProcessoParam;
//       numero = numeroComp.substring(0, 5) + numeroComp.substring(6, 12) + numeroComp.substring(15, 17);

//       for (j = 0; j < 2; j++) {
//         dig = "";
//         for (i = 0; i < 2; i++) {
//           tamanho = numero.length;
//           peso = 2;
//           resto = 0;
//           total = 0;
//           dv2 = "";
//           for (contador = tamanho; contador > 0; contador--) {
//             total = total + (peso * numero.substring(contador, contador - 1));
//             peso = peso + 1;
//           }
//           resto = total % 11;
//           dv = 11 - resto;
//           if (dv >= 10) { dv = 1 - resto }
//           if (dv2 = "") { dv2 = dv }
//           else { dv2 = (dv2 * 10) + dv }
//           dig = dig + dv
//           numero = numero + dv
//         }
//         if (dig != numeroComp.substring(18, 21)) {
//           erro = "Sim";
//           numero = numeroComp.substring(0, 5) + numeroComp.substring(6, 12) + numeroComp.substring(13, 17);
//         }
//         else {
//           j++
//           erro = ""
//         }
//       }
//       if (erro == "Sim") {
//         alert("Digito verificador incorreto.Valor Correto: " + dig);
//         return (false);
//       }
//     }
//   }



//   var checkOK = "0123456789-./-";
//   var checkStr = nuProcessoParam;
//   var allValid = true;
//   for (i = 0; i < checkStr.length; i++) {
//     ch = checkStr.charAt(i);
//     for (j = 0; j < checkOK.length; j++) {
//       if (ch == checkOK.charAt(j)) {
//         break;
//       }
//       if (j == checkOK.length) {
//         allValid = false;
//         break;
//       }
//     }
//   }
//   return true
// } 